/** Recipe Allergen & Dietary Matrix (Demo Mode) - a compliance-style grid for Range Planning:
 * every recipe in the catalogue down the left (image + name), every allergen/intolerance/
 * dietary requirement/lifestyle preference/nutritional claim code across the top, and each
 * cell highlighted where the recipe Contains, MayContain, is Suitable for, or is Certified
 * against that attribute. Reuses the same mocked catalogue as Recipe Bank/RecipeV2
 * (`recipeV2Data.ts`) - no typed client, no MSW handler, per the Demo Screen Structure pattern.
 * Net-new Demo screen (no equivalent list/detail exists elsewhere), gated behind the existing
 * `RecipeV2Read` permission used by the rest of the Recipe Bank/RecipeV2 surfaces. */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import RecipeImageTile from '../../components/shared/RecipeImageTile';
import { mockRecipesV2 } from '../../mocks/data/recipeV2Data';
import { DietaryAttributeStatus, DietaryCategory } from '../RecipeBank/types';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;
const NAME_COLUMN_WIDTH = 260;
const ATTRIBUTE_COLUMN_WIDTH = 56;

// Fixed left-to-right ordering so related attributes (all allergens, then all intolerances, etc.)
// group together across the top of the matrix rather than appearing in data-insertion order.
const CATEGORY_ORDER: DietaryCategory[] = [
  DietaryCategory.Allergen,
  DietaryCategory.Intolerance,
  DietaryCategory.DietaryRequirement,
  DietaryCategory.LifestylePreference,
  DietaryCategory.NutritionalClaim,
  DietaryCategory.SourcingSustainability,
];

// Statuses that represent a positive match worth flagging on the matrix (the recipe contains,
// may contain, or meets/is certified for the attribute). FreeFrom/NotSuitable are the "no claim"
// states and are rendered as a plain dash so the highlighted cells stand out.
const HIGHLIGHTED_STATUSES = new Set<DietaryAttributeStatus>([
  DietaryAttributeStatus.Contains,
  DietaryAttributeStatus.MayContain,
  DietaryAttributeStatus.Suitable,
  DietaryAttributeStatus.Certified,
]);

interface MatrixColumn {
  code: string;
  name: string;
  category: DietaryCategory;
}

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const RecipeAllergenMatrixPage = () => {
  const { t } = useTranslation('recipeAllergenMatrix');
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeCategories, setActiveCategories] = useState<Set<DietaryCategory>>(
    () => new Set(CATEGORY_ORDER),
  );

  const columns = useMemo<MatrixColumn[]>(() => {
    const byCode = new Map<string, MatrixColumn>();
    mockRecipesV2.forEach((recipe) => {
      recipe.dietaryAttributes.forEach((attr) => {
        if (!byCode.has(attr.code)) {
          byCode.set(attr.code, { code: attr.code, name: attr.name, category: attr.category });
        }
      });
    });
    return Array.from(byCode.values()).sort((a, b) => {
      const categoryDiff = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
      return categoryDiff !== 0 ? categoryDiff : a.name.localeCompare(b.name);
    });
  }, []);

  const categoryCounts = useMemo(() => {
    const counts = new Map<DietaryCategory, number>();
    columns.forEach((c) => counts.set(c.category, (counts.get(c.category) ?? 0) + 1));
    return counts;
  }, [columns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => activeCategories.has(c.category)),
    [columns, activeCategories],
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
        setDebouncedSearch(value);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const toggleCategory = (category: DietaryCategory) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const filteredRecipes = useMemo(() => {
    if (!debouncedSearch) return mockRecipesV2;
    const needle = debouncedSearch.toLowerCase();
    return mockRecipesV2.filter((recipe) => {
      const haystack = `${recipe.customerFacingName ?? ''} ${recipe.description}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [debouncedSearch]);

  const cellStyleForStatus = (status?: DietaryAttributeStatus) => {
    switch (status) {
      case DietaryAttributeStatus.Contains:
        return { bg: theme.palette.error.light, fg: theme.palette.error.dark, label: t('cell.contains', 'C') };
      case DietaryAttributeStatus.MayContain:
        return { bg: '#FFE0B2', fg: '#E65100', label: t('cell.mayContain', 'M') };
      case DietaryAttributeStatus.Certified:
        return { bg: theme.palette.success.main, fg: '#FFFFFF', label: t('cell.certified', '★') };
      case DietaryAttributeStatus.Suitable:
        return { bg: theme.palette.success.light, fg: theme.palette.success.dark, label: t('cell.suitable', '✓') };
      default:
        return null;
    }
  };

  return (
    <PageArea>
      <Stack width="100%"
        spacing={2}>
        <PageTitle subTitle={t('subtitle', 'Compare every recipe against allergens, intolerances, dietary requirements, lifestyle preferences and nutritional claims')}>
          {t('title', 'Recipe Allergen & Dietary Matrix')}
        </PageTitle>

        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <TextField
            size="small"
            placeholder={t('searchPlaceholder', 'Search recipes...')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <Close
                    sx={{ cursor: 'pointer', fontSize: 18 }}
                    onClick={() => handleSearchChange('')}
                  />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ minWidth: 240, mb: 0 }}
          />

          <QuickFilterDivider />

          <Stack direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap>
            {CATEGORY_ORDER.filter((category) => categoryCounts.has(category)).map((category) => (
              <Chip
                key={category}
                label={`${t(`category.${category}`, category)} (${categoryCounts.get(category)})`}
                color={activeCategories.has(category) ? 'primary' : 'default'}
                onClick={() => toggleCategory(category)}
              />
            ))}
          </Stack>
        </Stack>

        <Stack direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 1 }}>
          {([
            [DietaryAttributeStatus.Contains, t('cell.contains', 'C')],
            [DietaryAttributeStatus.MayContain, t('cell.mayContain', 'M')],
            [DietaryAttributeStatus.Suitable, t('cell.suitable', '✓')],
            [DietaryAttributeStatus.Certified, t('cell.certified', '★')],
          ] as [DietaryAttributeStatus, string][]).map(([status, label]) => {
            const style = cellStyleForStatus(status);
            return (
              <Stack key={status}
                direction="row"
                spacing={1}
                alignItems="center">
                <Box sx={{
                  width: 20, height: 20, borderRadius: '4px', backgroundColor: style?.bg, color: style?.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700,
                }}>
                  {label}
                </Box>
                <Typography variant="textXS"
                  color="text.secondary">
                  {t(`status.${status}`, status)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        <Section>
          <TableHeader>
            {t('matrix', 'Matrix')}
            <Pill text={`${filteredRecipes.length} ${t('recipes', 'Recipes')}`} />
          </TableHeader>
          {filteredRecipes.length === 0 ? (
            <Stack alignItems="center"
              sx={{ py: 8 }}>
              <Typography variant="textMD"
                color="text.secondary">
                {t('noResults', 'No recipes match the selected filters')}
              </Typography>
            </Stack>
          ) : (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small"
                stickyHeader
                sx={{ borderCollapse: 'separate' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky', left: 0, zIndex: 3, minWidth: NAME_COLUMN_WIDTH, backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      {t('recipeColumn', 'Recipe')}
                    </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.code}
                        align="center"
                        sx={{
                          minWidth: ATTRIBUTE_COLUMN_WIDTH, maxWidth: ATTRIBUTE_COLUMN_WIDTH, p: '4px', verticalAlign: 'bottom',
                        }}
                      >
                        <Tooltip title={`${column.name} (${t(`category.${column.category}`, column.category)})`}>
                          <Box sx={{
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            fontSize: '11px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            maxHeight: 140,
                            mx: 'auto',
                          }}>
                            {column.name}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecipes.map((recipe) => {
                    const displayName = recipe.customerFacingName ?? recipe.description;
                    return (
                      <TableRow key={recipe.id}
                        hover>
                        <TableCell
                          sx={{
                            position: 'sticky', left: 0, zIndex: 2, backgroundColor: theme.palette.background.paper, minWidth: NAME_COLUMN_WIDTH,
                          }}
                        >
                          <Stack direction="row"
                            spacing={1.5}
                            alignItems="center">
                            <Box sx={{ width: 40, height: 40, flexShrink: 0 }}>
                              <RecipeImageTile
                                imageUrl={recipe.imageUrl}
                                alt={displayName}
                                height={40}
                                sx={{ borderRadius: '6px' }}
                              />
                            </Box>
                            <Typography variant="textSM"
                              fontWeight={600}
                              sx={{
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: NAME_COLUMN_WIDTH - 64,
                              }}>
                              {displayName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {visibleColumns.map((column) => {
                          const attribute = recipe.dietaryAttributes.find((a) => a.code === column.code);
                          const style = attribute && HIGHLIGHTED_STATUSES.has(attribute.status)
                            ? cellStyleForStatus(attribute.status)
                            : null;
                          return (
                            <TableCell
                              key={column.code}
                              align="center"
                              sx={{ p: '4px' }}
                            >
                              {style ? (
                                <Tooltip title={`${column.name}: ${t(`status.${attribute!.status}`, attribute!.status)}`}>
                                  <Box sx={{
                                    width: 24, height: 24, borderRadius: '4px', backgroundColor: style.bg, color: style.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, mx: 'auto',
                                  }}>
                                    {style.label}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Typography variant="textXS"
                                  color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>
      </Stack>
    </PageArea>
  );
};

export default RecipeAllergenMatrixPage;
